import React, { useState, useEffect, useCallback } from 'react';
import emailjs from '@emailjs/browser';
import { emailjsConfig } from '../config/emailjs.config';
import GDPRModal from './GDPRModal';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    sector: '',
    message: ''
  });

  const [captcha, setCaptcha] = useState({ question: '', answer: 0 });
  const [captchaInput, setCaptchaInput] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGDPRModal, setShowGDPRModal] = useState(false);

  // Generar captcha matemático
  const generateCaptcha = useCallback(() => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({
      question: `${num1} + ${num2}`,
      answer: num1 + num2
    });
    setCaptchaInput('');
  }, []);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar consentimiento GDPR
    if (!gdprConsent) {
      const event = new CustomEvent('showNotification', {
        detail: {
          title: 'Consentimiento requerido',
          message: 'Debes aceptar la política de privacidad para continuar.'
        }
      });
      window.dispatchEvent(event);
      return;
    }
    
    // Validar captcha matemático
    if (parseInt(captchaInput) !== captcha.answer) {
      const event = new CustomEvent('showNotification', {
        detail: {
          title: 'Error de verificación',
          message: 'Por favor, resuelve correctamente el captcha.'
        }
      });
      window.dispatchEvent(event);
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);

    try {
      // Validar que la configuración esté completa
      if (
        !emailjsConfig ||
        !emailjsConfig.serviceID ||
        !emailjsConfig.templateID ||
        !emailjsConfig.publicKey ||
        emailjsConfig.serviceID === 'YOUR_SERVICE_ID' ||
        emailjsConfig.templateID === 'YOUR_TEMPLATE_ID' ||
        emailjsConfig.publicKey === 'YOUR_PUBLIC_KEY'
      ) {
        throw new Error('EmailJS no está configurado. Por favor, configura las credenciales en src/config/emailjs.config.js');
      }

      // Enviar email usando EmailJS
      await emailjs.send(
        emailjsConfig.serviceID,
        emailjsConfig.templateID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          company: formData.company,
          sector: formData.sector,
          message: formData.message,
          to_email: emailjsConfig.toEmail,
        },
        emailjsConfig.publicKey
      );

      // Mostrar notificación de éxito
      const event = new CustomEvent('showNotification', {
        detail: {
          title: '¡Mensaje enviado!',
          message: 'Gracias por tu interés. Nos pondremos en contacto contigo muy pronto.'
        }
      });
      window.dispatchEvent(event);

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        sector: '',
        message: ''
      });
      setGdprConsent(false);
      generateCaptcha();
    } catch (error) {
      console.error('Error al enviar el email:', error);
      const event = new CustomEvent('showNotification', {
        detail: {
          title: 'Error al enviar',
          message: 'Hubo un problema al enviar tu mensaje. Por favor, inténtalo de nuevo o contáctanos directamente.'
        }
      });
      window.dispatchEvent(event);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="cta-section" id="contacto">
      <div className="cta-content">
        <div className="section-tag contact-tag">Contacto</div>
        <h2 className="contact-title">Transforma tu negocio hoy</h2>
        <p className="contact-description">
          Convierte tu negocio abrazando la IA conversacional. Completa el formulario 
          y nos pondremos en contacto contigo.
        </p>
        <form className="cta-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Nombre completo</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Tu nombre"
              />
            </div>
            <div className="form-group">
              <label>Email corporativo</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="tu@empresa.com"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Teléfono</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+34 600 000 000"
              />
            </div>
            <div className="form-group">
              <label>Empresa</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder="Nombre de tu empresa"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Sector</label>
            <select
              name="sector"
              value={formData.sector}
              onChange={handleChange}
              required
            >
              <option value="">Selecciona tu sector</option>
              <option value="retail">Retail & Centros Comerciales</option>
              <option value="hospitality">Hostelería & Turismo</option>
              <option value="education">Educación</option>
              <option value="culture">Cultura & Museos</option>
              <option value="fitness">Fitness & Deporte</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div className="form-group">
            <label>Mensaje</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Cuéntanos sobre tu proyecto y necesidades..."
            />
          </div>
          <div className="form-group captcha-group">
            <label>Verificación de seguridad</label>
            <div className="captcha-container">
              <div className="captcha-question">
                <span>{captcha.question || 'Cargando...'} = </span>
                <button 
                  type="button" 
                  className="captcha-refresh"
                  onClick={generateCaptcha}
                  title="Generar nuevo captcha"
                >
                  🔄
                </button>
              </div>
              <input
                type="number"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                placeholder="?"
                required
                className="captcha-input"
              />
            </div>
          </div>
          <div className="form-group gdpr-consent-group">
            <label className="gdpr-consent-label">
              <input
                type="checkbox"
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                required
                className="gdpr-checkbox"
              />
             
            </label>
             <span className="gdpr-text">
                Acepto la <button type="button" onClick={(e) => { e.preventDefault(); setShowGDPRModal(true); }} className="gdpr-link-button">política de privacidad</button> y consiento el tratamiento de mis datos personales para que Myntar pueda contactarme en relación con mi consulta. Puedo retirar mi consentimiento en cualquier momento.
              </span>
          </div>
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Enviando...' : 'Solicitar Consulta'}
          </button>
        </form>
      </div>
      <GDPRModal isOpen={showGDPRModal} onClose={() => setShowGDPRModal(false)} />
    </section>
  );
};

export default Contact;
